import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  IndianRupee, TrendingUp, Users, Target, Clock,
  CheckCircle, AlertCircle, Loader2, CalendarDays
} from 'lucide-react';
import { API_URL } from '../utils/apiUrl';

const formatCurrency = (value) => {
  if (!value) return '₹0';
  return `₹${value.toLocaleString('en-IN')}`;
};

const MyIncentivesWidget = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('monthly');
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchIncentives();
  }, [period, token]);

  const fetchIncentives = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/incentives/my-incentives?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const result = await res.json();
        setData(result);
      }
    } catch (err) {
      console.error('Failed to fetch incentives:', err);
    } finally {
      setLoading(false);
    }
  };

  const periodLabels = {
    daily: 'Today',
    weekly: 'This Week',
    monthly: 'This Month',
    all: 'All Time'
  };

  if (loading) {
    return (
      <Card className="card-vault" data-testid="my-incentives-widget">
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-[#C5A059]" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="card-vault" data-testid="my-incentives-widget">
      <CardHeader className="border-b border-white/5">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-white flex items-center gap-2">
            <IndianRupee className="w-5 h-5 text-[#C5A059]" />
            My Incentives
          </CardTitle>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-32 bg-slate-800 border-white/10 h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="last_trading_day">Last Trading Day</SelectItem>
              <SelectItem value="weekly">This Week</SelectItem>
              <SelectItem value="monthly">This Month</SelectItem>
              <SelectItem value="all">All Time</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        {/* Total Incentive */}
        <div className="text-center mb-6 p-4 bg-gradient-to-r from-[#C5A059]/20 to-[#C5A059]/5 rounded-xl border border-[#C5A059]/30">
          <p className="text-sm text-slate-400 mb-1">{periodLabels[period]} Earnings</p>
          <p className="text-4xl font-bold text-[#C5A059]">
            {formatCurrency(data?.total_incentive || 0)}
          </p>
        </div>

        {/* Breakdown Cards */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="bg-emerald-500/10 rounded-lg p-3 border border-emerald-500/20">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-emerald-400" />
              <span className="text-xs text-emerald-300">Regular</span>
            </div>
            <p className="text-lg font-bold text-white">
              {data?.breakdown?.regular_meetings?.count || 0}
            </p>
            <p className="text-xs text-emerald-400">
              {formatCurrency(data?.breakdown?.regular_meetings?.amount || 0)}
            </p>
          </div>

          <div className="bg-amber-500/10 rounded-lg p-3 border border-amber-500/20">
            <div className="flex items-center gap-2 mb-2">
              <Target className="w-4 h-4 text-amber-400" />
              <span className="text-xs text-amber-300">Dormant</span>
            </div>
            <p className="text-lg font-bold text-white">
              {data?.breakdown?.dormant_meetings?.count || 0}
            </p>
            <p className="text-xs text-amber-400">
              {formatCurrency(data?.breakdown?.dormant_meetings?.amount || 0)}
            </p>
          </div>

          <div className="bg-[#C5A059]/10 rounded-lg p-3 border border-[#C5A059]/20">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-[#C5A059]" />
              <span className="text-xs text-[#C5A059]">Activations</span>
            </div>
            <p className="text-lg font-bold text-white">
              {data?.breakdown?.dormant_activations?.count || 0}
            </p>
            <p className="text-xs text-[#C5A059]">
              {formatCurrency(data?.breakdown?.dormant_activations?.amount || 0)}
            </p>
          </div>
        </div>

        {/* Pending Activations */}
        {data?.pending_activations?.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
              <Clock className="w-4 h-4 text-yellow-400" />
              Pending Activations ({data.pending_activations.length})
            </h4>
            <div className="space-y-2 max-h-32 overflow-auto">
              {data.pending_activations.slice(0, 5).map((p, idx) => {
                const deadline = new Date(p.deadline_date);
                const today = new Date();
                const daysLeft = Math.ceil((deadline - today) / (1000 * 60 * 60 * 24));
                
                return (
                  <div 
                    key={idx} 
                    className="flex items-center justify-between p-2 bg-yellow-500/10 rounded border border-yellow-500/20"
                  >
                    <div>
                      <p className="text-sm text-white font-medium">
                        {p.client_name || p.client_code}
                        {p.client_name && <span className="text-slate-500 ml-1 text-xs">({p.client_code})</span>}
                      </p>
                      <p className="text-xs text-slate-400">
                        Meeting: {new Date(p.meeting_date).toLocaleDateString()}
                      </p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                      daysLeft <= 7 ? 'bg-red-500/20 text-red-400 border border-red-500/30' :
                      daysLeft <= 14 ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' :
                      'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                    }`} data-testid={`mi-widget-pending-days-${idx}`}>
                      {daysLeft} days left
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Recent Activated Clients */}
        {data?.activated_clients?.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-400" />
              Recent Activations
            </h4>
            <div className="space-y-2 max-h-32 overflow-auto">
              {data.activated_clients.slice(0, 5).map((a, idx) => (
                <div 
                  key={idx}
                  className="flex items-center justify-between p-2 bg-emerald-500/10 rounded border border-emerald-500/20"
                >
                  <div>
                    <p className="text-sm text-white font-medium">
                      {a.client_name || a.client_code}
                      {a.client_name && <span className="text-slate-500 ml-1 text-xs">({a.client_code})</span>}
                    </p>
                    <p className="text-xs text-slate-400">
                      {new Date(a.activation_date).toLocaleDateString()}
                    </p>
                  </div>
                  <span className="text-emerald-400 font-medium">
                    +{formatCurrency(a.incentive_amount)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty state */}
        {!data?.total_incentive && !data?.pending_activations?.length && (
          <div className="text-center py-4 text-slate-500">
            <CalendarDays className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No incentives earned {periodLabels[period].toLowerCase()}</p>
            <p className="text-xs mt-1">Log client meetings to earn incentives!</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MyIncentivesWidget;
